import { currencySymbol } from '@hue/design-system';
import type {
  Currency,
  InventoryBaseUnit,
  PurchasingActivityFilter,
  PurchasingKindFilter,
  PurchasingOfferingKindFilter,
  SupplierKind,
  SupplierOffering,
  SupplierOfferingKind,
} from '../../lib/api.js';

export interface PurchasingCapabilities {
  canManageSuppliers: boolean;
  canManageOfferings: boolean;
  canManageOrders?: boolean;
  canManageReceipts?: boolean;
}

export const supplierKindLabels: Record<SupplierKind, string> = {
  PERSON: 'Pessoa',
  COMPANY: 'Empresa',
};
export const supplierKindOptions = (Object.keys(supplierKindLabels) as SupplierKind[]).map(
  (value) => ({ value, label: supplierKindLabels[value] }),
);
export const offeringKindLabels: Record<SupplierOfferingKind, string> = {
  PRODUCT: 'Produto de estoque',
  SERVICE: 'Serviço',
};
export const offeringKindOptions = (Object.keys(offeringKindLabels) as SupplierOfferingKind[]).map(
  (value) => ({ value, label: offeringKindLabels[value] }),
);
export const activityOptions: Array<{ value: PurchasingActivityFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Arquivados' },
];
export const supplierKindFilterOptions: Array<{ value: PurchasingKindFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'COMPANY', label: 'Empresas' },
  { value: 'PERSON', label: 'Pessoas' },
];
export const offeringKindFilterOptions: Array<{
  value: PurchasingOfferingKindFilter;
  label: string;
}> = [
  { value: 'ALL', label: 'Todas' },
  { value: 'PRODUCT', label: 'Produtos' },
  { value: 'SERVICE', label: 'Serviços' },
];

export const currencyOptions: Array<{ value: Currency; label: string }> = [
  { value: 'BRL', label: 'Real (BRL)' },
  { value: 'USD', label: 'Dólar (USD)' },
  { value: 'ARS', label: 'Peso argentino (ARS)' },
];

export function shortUnit(unit: InventoryBaseUnit): string {
  return { UNIT: 'unidades', MILLILITER: 'ml', GRAM: 'g' }[unit];
}

/// `500.000000` é o que o banco guarda; `500` é o que a pessoa escreveu.
export function trimQuantity(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/u, '') : value;
}

/// Documento só aparece formatado: quem cadastrou digitou com pontuação, e ler catorze dígitos
/// colados é trabalho que a tela não precisa devolver para ninguém.
export function formatDocument(document: string | null): string | null {
  if (!document) return null;
  if (document.length === 11) {
    return document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/u, '$1.$2.$3-$4');
  }
  if (document.length === 14) {
    return document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/u, '$1.$2.$3/$4-$5');
  }
  return document;
}

export function formatPhone(phone: string | null): string | null {
  if (!phone) return null;
  if (phone.length === 11) return phone.replace(/^(\d{2})(\d{5})(\d{4})$/u, '($1) $2-$3');
  if (phone.length === 10) return phone.replace(/^(\d{2})(\d{4})(\d{4})$/u, '($1) $2-$3');
  return phone;
}

const SCALE = 6;

function scaledQuantity(value: string): bigint {
  const [whole = '0', fraction = ''] = value.split('.');
  return BigInt(whole + fraction.padEnd(SCALE, '0').slice(0, SCALE));
}

/// Quanto custa uma unidade-base — o número que permite comparar "caixa com 12" de um fornecedor
/// com "frasco avulso" de outro.
///
/// Derivado, nunca guardado: um segundo número no banco envelheceria sozinho na primeira mudança de
/// preço. A conta acontece em inteiro, porque dividir dinheiro em ponto flutuante erra centavo.
export function unitCostLabel(offering: SupplierOffering): string | null {
  if (!offering.packageBaseQuantity) return null;
  const quantity = scaledQuantity(offering.packageBaseQuantity);
  if (quantity <= 0n) return null;
  const half = quantity / 2n;
  const centsTimesTenThousand = (BigInt(offering.unitPriceCents) * 10n ** 10n + half) / quantity;
  return `${currencySymbol(offering.currency)} ${decimalOf(centsTimesTenThousand)}`;
}

/// O valor chega em centavos multiplicados por 10.000, ou seja, com seis casas de moeda. Mostra as
/// que existem, nunca menos de duas: "0,035" é o preço de um mililitro, e arredondá-lo para "0,04"
/// apagaria a diferença entre dois fornecedores.
function decimalOf(value: bigint): string {
  const digits = value.toString().padStart(SCALE + 1, '0');
  const whole = digits.slice(0, -SCALE);
  const fraction = digits.slice(-SCALE).replace(/0+$/u, '').padEnd(2, '0');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/gu, '.');
  return `${grouped},${fraction}`;
}
